import {
  mapTestItemStatusToReport,
  pickLocalizedText,
  timeToCompleteToMinutes,
} from 'shared/modals/testCreate/hydrateTestFromApi'
import type { TestReportDisplayStatus } from 'shared/modals/testCreate/hydrateTestFromApi'

export type TestsAdminCardStatus = TestReportDisplayStatus

export type TestsAdminTestCard = {
  id: string
  status: TestsAdminCardStatus
  course: string
  module: string
  moduleId: string
  title: string
  description: string
  authorName: string
  publishedAtIso: string | null
  attemptsCount: number
  totalAttemptsCount: number
  questionsCount: number
  passPercent: number
  durationMin: number
}

export function extractManagerTestItemsListArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw
  }
  if (raw !== null && typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    const nested =
      o.data ?? o.Data ?? o.items ?? o.Items ?? o.result ?? o.Result
    if (Array.isArray(nested)) {
      return nested
    }
  }
  return []
}

function readNumberField(
  r: Record<string, unknown>,
  keys: string[]
): number {
  for (const k of keys) {
    const v = r[k]
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v
    }
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) {
        return n
      }
    }
  }
  return 0
}

function clampPercent(n: number): number {
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  return Math.min(100, Math.floor(n))
}

function parseAdditionalInfo(
  row: Record<string, unknown>
): Record<string, unknown> | null {
  const raw =
    row.additionalInfo ??
    row.AdditionalInfo

  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  return null
}

function parseLocalizedNameArray(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
  }
  return []
}

function parseAuthorName(row: Record<string, unknown>, preferredLang: string): string {
  const accountRaw = row.account ?? row.Account
  if (accountRaw !== null && typeof accountRaw === 'object' && !Array.isArray(accountRaw)) {
    const account = accountRaw as Record<string, unknown>
    const surName = pickLocalizedText(account.surName ?? account.SurName, preferredLang).trim()
    const name = pickLocalizedText(account.name ?? account.Name, preferredLang).trim()
    const middleName = pickLocalizedText(
      account.middleName ?? account.MiddleName,
      preferredLang
    ).trim()

    const fullName = [surName, name, middleName].filter(Boolean).join(' ').trim()
    if (fullName) {
      return fullName
    }
  }

  const fallback =
    row.authorName ??
    row.AuthorName ??
    row.author ??
    row.Author
  return typeof fallback === 'string' ? fallback.trim() : ''
}

function parseIsoDate(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    const date = new Date(value.trim())
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString()
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString()
    }
  }
  return null
}

export function mapManagerTestListRowToCard(
  row: unknown,
  preferredLang: string
): TestsAdminTestCard | null {
  if (row === null || typeof row !== 'object') {
    return null
  }
  const r = row as Record<string, unknown>
  const idRaw = r.id ?? r.Id ?? r.testItemId ?? r.TestItemId
  const id = typeof idRaw === 'string' ? idRaw.trim() : ''
  if (!id) {
    return null
  }

  const titleParts = r.title ?? r.Title
  const descParts = r.description ?? r.Description
  const title = pickLocalizedText(titleParts, preferredLang)
  const description = pickLocalizedText(descParts, preferredLang)

  const passingScore = readNumberField(r, ['passingScore', 'PassingScore'])
  const questionsCount = readNumberField(r, [
    'numberOfQuestions',
    'NumberOfQuestions',
  ])

  const timeRaw = r.timeToComplete ?? r.TimeToComplete
  const minsParsed = parseInt(timeToCompleteToMinutes(timeRaw), 10)
  const durationMin =
    Number.isFinite(minsParsed) && minsParsed >= 0 ? minsParsed : 0

  const statusRaw = r.status ?? r.Status
  const status = mapTestItemStatusToReport(statusRaw)

  const additionalInfo = parseAdditionalInfo(r)
  const course = pickLocalizedText(
    parseLocalizedNameArray(additionalInfo?.courseName ?? additionalInfo?.CourseName),
    preferredLang
  )
  const module = pickLocalizedText(
    parseLocalizedNameArray(additionalInfo?.projectName ?? additionalInfo?.ProjectName),
    preferredLang
  )

  const moduleIdRaw = additionalInfo?.projectId ?? additionalInfo?.ProjectId
  const moduleId = typeof moduleIdRaw === 'string' ? moduleIdRaw.trim() : ''

  const attemptsCount = Math.max(
    0,
    Math.floor(
      readNumberField(r, [
        'attemptsCount',
        'AttemptsCount',
        'attempsCount',
        'AttempsCount',
        'numberOfAttempts',
        'NumberOfAttempts',
      ])
    )
  )

  const totalAttemptsCount = Math.max(
    0,
    Math.floor(
      readNumberField(r, ['totalAttemptsCount', 'TotalAttemptsCount'])
    )
  )

  const publishedAtIso = parseIsoDate(
    r.published ??
      r.Published ??
      r.publishedAt ??
      r.PublishedAt ??
      r.publicationDate ??
      r.PublicationDate ??
      r.created ??
      r.Created ??
      r.createdAt ??
      r.CreatedAt
  )

  return {
    id,
    status,
    course,
    module,
    moduleId,
    title,
    description,
    authorName: parseAuthorName(r, preferredLang),
    publishedAtIso,
    attemptsCount,
    totalAttemptsCount,
    questionsCount,
    passPercent: clampPercent(passingScore),
    durationMin,
  }
}

const ALL_MANAGER_TEST_ITEM_STATUSES: number[] = [0, 1, 2]

export function managerTestStatusesForTab(
  tab: 'all' | TestsAdminCardStatus
): number[] {
  if (tab === 'all') {
    return ALL_MANAGER_TEST_ITEM_STATUSES
  }
  if (tab === 'published') {
    return [1]
  }
  if (tab === 'archived') {
    return [2]
  }
  return [0]
}
import {
  mapTestItemStatusToReport,
  pickLocalizedText,
  timeToCompleteToMinutes,
} from 'shared/modals/testCreate/hydrateTestFromApi'
import type { TestReportDisplayStatus } from 'shared/modals/testCreate/hydrateTestFromApi'

export type TestsAdminCardStatus = TestReportDisplayStatus

export type TestsAdminTestCard = {
  id: string
  status: TestsAdminCardStatus
  course: string
  module: string
  moduleId: string
  title: string
  description: string
  authorName: string
  publishedAtIso: string | null
  attemptsCount: number
  totalAttemptsCount: number
  questionsCount: number
  passPercent: number
  durationMin: number
}

export function extractManagerTestItemsListArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw
  }
  if (raw !== null && typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    const nested =
      o.data ?? o.Data ?? o.items ?? o.Items ?? o.result ?? o.Result
    if (Array.isArray(nested)) {
      return nested
    }
  }
  return []
}

function readNumberField(
  r: Record<string, unknown>,
  keys: string[]
): number {
  for (const k of keys) {
    const v = r[k]
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v
    }
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) {
        return n
      }
    }
  }
  return 0
}

function clampPercent(n: number): number {
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  return Math.min(100, Math.floor(n))
}

function parseAdditionalInfo(
  row: Record<string, unknown>
): Record<string, unknown> | null {
  const raw =
    row.additionalInfo ??
    row.AdditionalInfo

  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  return null
}

function parseLocalizedNameArray(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
  }
  return []
}

function parseAuthorName(row: Record<string, unknown>, preferredLang: string): string {
  const accountRaw = row.account ?? row.Account
  if (accountRaw !== null && typeof accountRaw === 'object' && !Array.isArray(accountRaw)) {
    const account = accountRaw as Record<string, unknown>
    const surName = pickLocalizedText(account.surName ?? account.SurName, preferredLang).trim()
    const name = pickLocalizedText(account.name ?? account.Name, preferredLang).trim()
    const middleName = pickLocalizedText(
      account.middleName ?? account.MiddleName,
      preferredLang
    ).trim()

    const fullName = [surName, name, middleName].filter(Boolean).join(' ').trim()
    if (fullName) {
      return fullName
    }
  }

  const fallback =
    row.authorName ??
    row.AuthorName ??
    row.author ??
    row.Author
  return typeof fallback === 'string' ? fallback.trim() : ''
}

function parseIsoDate(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    const date = new Date(value.trim())
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString()
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString()
    }
  }
  return null
}

export function mapManagerTestListRowToCard(
  row: unknown,
  preferredLang: string
): TestsAdminTestCard | null {
  if (row === null || typeof row !== 'object') {
    return null
  }
  const r = row as Record<string, unknown>
  const idRaw = r.id ?? r.Id ?? r.testItemId ?? r.TestItemId
  const id = typeof idRaw === 'string' ? idRaw.trim() : ''
  if (!id) {
    return null
  }

  const titleParts = r.title ?? r.Title
  const descParts = r.description ?? r.Description
  const title = pickLocalizedText(titleParts, preferredLang)
  const description = pickLocalizedText(descParts, preferredLang)

  const passingScore = readNumberField(r, ['passingScore', 'PassingScore'])
  const questionsCount = readNumberField(r, [
    'numberOfQuestions',
    'NumberOfQuestions',
  ])

  const timeRaw = r.timeToComplete ?? r.TimeToComplete
  const minsParsed = parseInt(timeToCompleteToMinutes(timeRaw), 10)
  const durationMin =
    Number.isFinite(minsParsed) && minsParsed >= 0 ? minsParsed : 0

  const statusRaw = r.status ?? r.Status
  const status = mapTestItemStatusToReport(statusRaw)

  const additionalInfo = parseAdditionalInfo(r)
  const course = pickLocalizedText(
    parseLocalizedNameArray(additionalInfo?.courseName ?? additionalInfo?.CourseName),
    preferredLang
  )
  const module = pickLocalizedText(
    parseLocalizedNameArray(additionalInfo?.projectName ?? additionalInfo?.ProjectName),
    preferredLang
  )

  const moduleIdRaw = additionalInfo?.projectId ?? additionalInfo?.ProjectId
  const moduleId = typeof moduleIdRaw === 'string' ? moduleIdRaw.trim() : ''

  const attemptsCount = Math.max(
    0,
    Math.floor(
      readNumberField(r, [
        'attemptsCount',
        'AttemptsCount',
        'attempsCount',
        'AttempsCount',
        'numberOfAttempts',
        'NumberOfAttempts',
      ])
    )
  )

  const totalAttemptsCount = Math.max(
    0,
    Math.floor(
      readNumberField(r, ['totalAttemptsCount', 'TotalAttemptsCount'])
    )
  )

  const publishedAtIso = parseIsoDate(
    r.published ??
      r.Published ??
      r.publishedAt ??
      r.PublishedAt ??
      r.publicationDate ??
      r.PublicationDate ??
      r.created ??
      r.Created ??
      r.createdAt ??
      r.CreatedAt
  )

  return {
    id,
    status,
    course,
    module,
    moduleId,
    title,
    description,
    authorName: parseAuthorName(r, preferredLang),
    publishedAtIso,
    attemptsCount,
    totalAttemptsCount,
    questionsCount,
    passPercent: clampPercent(passingScore),
    durationMin,
  }
}

const ALL_MANAGER_TEST_ITEM_STATUSES: number[] = [0, 1, 2]

export function managerTestStatusesForTab(
  tab: 'all' | TestsAdminCardStatus
): number[] {
  if (tab === 'all') {
    return ALL_MANAGER_TEST_ITEM_STATUSES
  }
  if (tab === 'published') {
    return [1]
  }
  if (tab === 'archived') {
    return [2]
  }
  return [0]
}
